import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  DragOverlay,
  useDndContext,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HolderOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { _getHeaderColor, DEFAULT_ACCENT } from "../../utils/helpers";
import styles from "./styles.module.css";

/**
 * The board runs on @dnd-kit rather than @hello-pangea/dnd for one reason:
 * nested scroll containers. Each column scrolls its own cards while the board
 * scrolls sideways, and hello-pangea supports exactly one scroll container per
 * droppable — it even warns «unsupported nested scroll container detected» and
 * then keeps using the drop targets it measured at lift, so every column the
 * board scrolled into mid-drag refused the card. dnd-kit re-measures droppables
 * while dragging and auto-scrolls each scrollable ancestor in turn, which is
 * what makes «drag to the edge, reach the far column, drop it there» work.
 */

const SCROLL_STEP = 300;

/* dnd-kit scrolls `acceleration × penetration` pixels every `interval` ms,
 * where penetration is how far into the threshold band the dragged card has
 * reached. The default 10 (and the 14 tried first) works out to ~2000-2800px
 * per second at the edge, which shot the board past two or three columns at a
 * time. A gentler acceleration over a wider band ramps up instead of snapping,
 * and the short interval is what keeps the movement itself smooth. */
/* dnd-kit is left in charge of the vertical scroll inside a column only; the
 * board's sideways scroll is driven by useEdgeAutoScroll below. Its own x
 * scrolling steps straight to a constant speed the moment the pointer enters
 * the band and only recomputes when the pointer moves again, which is what
 * made the board bolt to the last column. */
const AUTO_SCROLL_VERTICAL = {
  /* x stays at a sane band rather than 0: the board scroller is excluded by
   * canScroll, and a zero-width threshold would divide by zero for any other
   * horizontally scrollable ancestor that ever shows up. */
  threshold: { x: 0.2, y: 0.25 },
  acceleration: 10,
  interval: 5,
};

/* Sideways pull: how wide the band along each edge of the board is, the top
 * speed in pixels per frame once the pointer is right at the edge (~600px/s),
 * and how fast the current speed chases that target. The easing is the whole
 * point — speed ramps up and down over a handful of frames instead of
 * switching on, and the curve is quadratic so grazing the band barely moves. */
const EDGE_BAND = 120;
const EDGE_MAX_SPEED = 17;
const EDGE_EASING = 0.16;

/**
 * Scrolls the board sideways while a drag hovers near its edge.
 *
 * Runs on requestAnimationFrame so every step lands on a frame, and carries a
 * fractional scroll position of its own: assigning scrollLeft rounds, so
 * accumulating there would swallow the slow end of the ramp entirely.
 */
const useEdgeAutoScroll = (scrollerRef, isDragging) => {
  const pointerX = useRef(null);
  const velocity = useRef(0);
  const position = useRef(0);
  const frame = useRef(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!isDragging || !el) return undefined;

    position.current = el.scrollLeft;
    velocity.current = 0;
    pointerX.current = null;

    const onPointer = (event) => {
      const point = event.touches?.[0] || event;
      if (typeof point.clientX === "number") pointerX.current = point.clientX;
    };

    const step = () => {
      frame.current = requestAnimationFrame(step);

      const x = pointerX.current;
      if (x == null) return;

      const { left, right } = el.getBoundingClientRect();
      let target = 0;
      if (x < left + EDGE_BAND) {
        const depth = Math.min(1, (left + EDGE_BAND - x) / EDGE_BAND);
        target = -EDGE_MAX_SPEED * depth * depth;
      } else if (x > right - EDGE_BAND) {
        const depth = Math.min(1, (x - (right - EDGE_BAND)) / EDGE_BAND);
        target = EDGE_MAX_SPEED * depth * depth;
      }

      velocity.current += (target - velocity.current) * EDGE_EASING;

      if (Math.abs(velocity.current) < 0.08) {
        /* Idle: let go of the position so a manual scroll isn't fought. */
        velocity.current = 0;
        position.current = el.scrollLeft;
        return;
      }

      const max = el.scrollWidth - el.clientWidth;
      position.current = Math.max(
        0,
        Math.min(max, position.current + velocity.current),
      );
      el.scrollLeft = position.current;
    };

    window.addEventListener("pointermove", onPointer, true);
    window.addEventListener("mousemove", onPointer, true);
    window.addEventListener("touchmove", onPointer, {
      capture: true,
      passive: true,
    });
    frame.current = requestAnimationFrame(step);

    return () => {
      window.removeEventListener("pointermove", onPointer, true);
      window.removeEventListener("mousemove", onPointer, true);
      window.removeEventListener("touchmove", onPointer, true);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      pointerX.current = null;
      velocity.current = 0;
    };
  }, [isDragging, scrollerRef]);
};

/* Droppables are re-measured on every move, so a column the board just
 * scrolled into is where the pointer thinks it is. */
const MEASURING = { droppable: { strategy: MeasuringStrategy.Always } };

const COL = "kb-col:";
const CARD = "kb-card:";
const BODY = "kb-body:";

/**
 * Re-measures the drop targets while the board scrolls under the pointer.
 *
 * dnd-kit measures droppables when the drag starts and then leaves them alone,
 * which is fine when the pointer does the moving — but during auto-scroll the
 * pointer stands still and the columns slide beneath it, so the recorded rects
 * go stale and the drag stays stuck on whichever column it was over until some
 * unrelated re-render happened to refresh them (the twitchy jumps). One
 * re-measure per frame, only while something is being dragged, keeps the
 * targets under the pointer honest.
 */
const ScrollMeasureSync = ({ rootRef }) => {
  const { active, measureDroppableContainers } = useDndContext();

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !active) return undefined;

    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        measureDroppableContainers();
      });
    };

    /* Capture, because scroll events do not bubble: this one listener catches
     * the board scrolling sideways and any column scrolling its own cards. */
    el.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll, { capture: true });
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [active, measureDroppableContainers, rootRef]);

  return null;
};

const DefaultEmpty = ({ isDraggingOver }) => (
  <div className={`${styles.empty} ${isDraggingOver ? styles.emptyActive : ""}`}>
    <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="6" width="24" height="4" rx="2" fill="currentColor" opacity="0.16" />
      <rect x="4" y="14" width="18" height="4" rx="2" fill="currentColor" opacity="0.11" />
      <rect x="4" y="22" width="12" height="4" rx="2" fill="currentColor" opacity="0.07" />
    </svg>
    <span className={styles.emptyText}>موردی وجود ندارد</span>
  </div>
);

const columnKeyOfCard = (map, cardKey) =>
  Object.keys(map).find((key) => map[key].includes(cardKey));

const columnKeyOfOver = (map, overKey) => {
  if (!overKey) return undefined;
  if (overKey.startsWith(BODY)) return overKey.slice(BODY.length);
  if (overKey.startsWith(COL)) return overKey.slice(COL.length);
  return columnKeyOfCard(map, overKey);
};

const SortableCard = ({ id, card, column, disabled, renderCard }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      disabled,
      data: { type: "card", columnKey: String(column.id) },
    });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        /* The travelling card is drawn by the drag overlay, so what stays
         * behind is just the gap it will drop into. */
        opacity: isDragging ? 0 : undefined,
      }}
      {...attributes}
      {...listeners}
    >
      {renderCard(card, { isDragging, column })}
    </div>
  );
};

/**
 * Where the card will land, drawn as a line between two cards.
 *
 * A card crossing into another column does not get slotted into that column's
 * list while it travels — doing so reflows the column under the pointer and
 * fights the auto-scroll (see `handleDragOver`). The cost was that the cards
 * never parted, so there was nothing to aim at: every drop looked like it was
 * going to the bottom, and people stopped trying to place it. This says where
 * it is going without moving anything.
 */
const ColumnBody = ({
  column,
  cards,
  cardIds,
  cardsDraggable,
  isDraggingOver,
  insertIndex,
  renderCard,
  renderEmpty,
  renderFooter,
}) => {
  const { setNodeRef } = useDroppable({
    id: `${BODY}${column.id}`,
    data: { type: "body", columnKey: String(column.id) },
  });

  const bodyRef = useRef(null);
  const listRef = useRef(null);
  const [lineTop, setLineTop] = useState(null);

  const setRefs = (node) => {
    bodyRef.current = node;
    setNodeRef(node);
  };

  /* The line is one node that moves, not a node per slot: inserted into the
     flow it shoved every card below it down by its own height, so each change
     of mind jolted the whole column. Positioned instead, it takes no space and
     its `top` transitions, so it glides between the cards. Measured in layout
     rather than render because only the DOM knows how tall these cards are. */
  useLayoutEffect(() => {
    if (insertIndex == null || !listRef.current) {
      setLineTop(null);
      return;
    }

    const items = listRef.current.children;
    if (!items.length) {
      setLineTop(0);
      return;
    }

    const at = items[insertIndex];
    setLineTop(
      at
        ? at.offsetTop
        : items[items.length - 1].offsetTop +
            items[items.length - 1].offsetHeight,
    );
  }, [insertIndex, cards.length]);

  return (
    <div
      ref={setRefs}
      className={`${styles.columnBody} ${
        isDraggingOver ? styles.columnBodyOver : ""
      }`}
    >
      {lineTop != null && (
        <div
          className={styles.dropLine}
          style={{ top: lineTop }}
          aria-hidden="true"
        />
      )}
      <div ref={listRef}>
        <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
          {cards.length === 0
            ? renderEmpty(column, { isDraggingOver })
            : cards.map((card, index) => (
                <SortableCard
                  key={cardIds[index]}
                  id={cardIds[index]}
                  card={card}
                  column={column}
                  disabled={!cardsDraggable}
                  renderCard={renderCard}
                />
              ))}
        </SortableContext>
      </div>
      {renderFooter && renderFooter(column)}
    </div>
  );
};

const SortableColumn = ({
  column,
  width,
  draggable,
  renderHeaderActions,
  renderColumnTop,
  renderColumnFooter,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: `${COL}${column.id}`,
      disabled: !draggable,
      data: { type: "column", columnKey: String(column.id) },
    });

  return (
    <div
      ref={setNodeRef}
      className={styles.column}
      dir="rtl"
      style={{
        width,
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : undefined,
        zIndex: isDragging ? 3 : undefined,
      }}
    >
      <div
        className={`${styles.columnHeader} ${
          draggable ? styles.columnHeaderDraggable : ""
        }`}
        {...(draggable ? attributes : {})}
        {...(draggable ? listeners : {})}
      >
        {draggable && (
          <span className={styles.dragDots}>
            <HolderOutlined />
          </span>
        )}
        <span
          className={styles.columnTitle}
          style={{
            background: column.color || "var(--ds-surface-2)",
            color: column.color
              ? _getHeaderColor(column.color)
              : "var(--ds-text-secondary)",
          }}
        >
          {column.title}
        </span>
        <span className={styles.columnCount}>{column.tasks?.length || 0}</span>
        {renderHeaderActions && (
          <div
            className={styles.columnHeaderActions}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {renderHeaderActions(column)}
          </div>
        )}
      </div>
      {/* Between the header and the cards, outside the scroller like the footer:
          what is added from the column's «+» is looked for at the top. */}
      {renderColumnTop && (
        <div className={styles.columnTop}>{renderColumnTop(column)}</div>
      )}
      {children}
      {/* Outside the scrolling body on purpose: a composer that scrolls away
          with the cards is one you have to go looking for. */}
      {renderColumnFooter && (
        <div className={styles.columnFooter}>{renderColumnFooter(column)}</div>
      )}
    </div>
  );
};

const KanbanBoard = ({
  columns = [],
  getCardId = (card) => card.id,
  renderCard,
  renderHeaderActions,
  renderFooter,
  renderColumnTop,
  renderColumnFooter,
  renderEmpty,
  onColumnMove,
  onCardMove,
  onDragStart,
  onDragEnd,
  accent = DEFAULT_ACCENT,
  isMobile = false,
  columnWidth,
  trailing,
  busy = false,
  cardsDraggable = true,
  dataTour,
}) => {
  const width = columnWidth || (isMobile ? 236 : 308);

  /* The board reads right-to-left inside an ltr scroller, so the columns are
   * rendered reversed and the indexes handed back to the consumer are flipped
   * again. */
  const lastColumn = columns.length - 1;
  const toColumnIndex = (displayIndex) => lastColumn - displayIndex;
  const displayColumns = useMemo(() => [...columns].reverse(), [columns]);

  const { columnById, cardById, baseColumnIds, baseCardIds } = useMemo(() => {
    const colMap = new Map();
    const cardMap = new Map();
    const cardIds = {};

    columns.forEach((column) => {
      colMap.set(String(column.id), column.id);
      cardIds[String(column.id)] = (column.tasks || []).map((card) => {
        const key = `${CARD}${getCardId(card)}`;
        cardMap.set(key, card);
        return key;
      });
    });

    return {
      columnById: colMap,
      cardById: cardMap,
      baseColumnIds: displayColumns.map((column) => `${COL}${column.id}`),
      baseCardIds: cardIds,
    };
  }, [columns, displayColumns, getCardId]);

  const [activeType, setActiveType] = useState(null);
  const [activeCardKey, setActiveCardKey] = useState(null);
  const [overColumnKey, setOverColumnKey] = useState(null);
  /* Where the line sits in the hovered column while a card crosses into it. */
  const [dropIndex, setDropIndex] = useState(null);

  const sensors = useSensors(
    /* A card is a click target too (it opens the task), so a drag has to travel
     * a few pixels before it counts, and a touch has to dwell — that is what
     * keeps a column scrollable with a finger. */
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    const collisions = pointerCollisions.length
      ? pointerCollisions
      : rectIntersection(args);
    return getFirstCollision(collisions) ? collisions : [];
  }, []);

  const boardRef = useRef(null);
  const scrollerRef = useRef(null);

  useEdgeAutoScroll(scrollerRef, Boolean(activeType));

  /* The board scroller is handled by the hook above, so dnd-kit is told to
   * leave it alone and only scroll the column under the pointer. */
  const canScroll = useCallback(
    (element) => element !== scrollerRef.current,
    [],
  );
  const autoScroll = useMemo(
    () => ({ ...AUTO_SCROLL_VERTICAL, canScroll }),
    [canScroll],
  );

  const [scrollState, setScrollState] = useState({
    earlier: false,
    later: false,
  });

  const syncScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = el.scrollLeft;
    setScrollState({ earlier: pos < max - 1, later: pos > 1 });
  }, []);

  const scrollBy = (delta) =>
    scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    syncScrollState();
    el.addEventListener("scroll", syncScrollState, { passive: true });
    const ro = new ResizeObserver(syncScrollState);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", syncScrollState);
      ro.disconnect();
    };
  }, [syncScrollState, columns.length]);

  const didPinToStart = useRef(false);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || didPinToStart.current || columns.length === 0) return;
    el.scrollLeft = el.scrollWidth;
    didPinToStart.current = true;
    syncScrollState();
  }, [columns.length, syncScrollState]);

  const resetDrag = () => {
    setActiveType(null);
    setActiveCardKey(null);
    setOverColumnKey(null);
    setDropIndex(null);
  };

  const handleDragStart = ({ active }) => {
    const activeKey = String(active.id);
    const isCard = activeKey.startsWith(CARD);

    setActiveType(isCard ? "card" : "column");
    setActiveCardKey(isCard ? activeKey : null);

    onDragStart?.();
  };

  /**
   * Only the highlight moves while dragging.
   *
   * Sliding the card into the hovered column's list the moment it is hovered
   * (the usual multi-container recipe) fights the auto-scroll: the insert
   * reflows the column, the reflow moves what is under the pointer, and the
   * card is pulled back out again — so the board could never scroll past a
   * column that had cards in it. The travelling card is drawn by the overlay
   * anyway; the column it will land in says so by lighting up, and the lists
   * themselves are left alone until the drop.
   */
  const handleDragOver = ({ active, over }) => {
    const activeKey = String(active.id);
    if (!over || !activeKey.startsWith(CARD)) return;

    const plan = planDrop(active, over);
    setOverColumnKey(plan?.targetKey ?? null);

    /* Only for a card crossing into another column. Inside its own column the
     * sortable strategy already opens the gap, and a line as well would be two
     * answers to the same question. */
    setDropIndex(
      plan && plan.targetKey !== plan.originKey ? plan.toIndex : null,
    );
  };

  /**
   * Where the dragged card would land, given what it is currently over.
   *
   * Both the drop and the line that previews it read this, so the preview
   * cannot promise a position the drop then ignores — they are the same
   * arithmetic on the same inputs.
   */
  const planDrop = useCallback(
    (active, over) => {
      const activeKey = String(active.id);
      const overKey = String(over.id);

      const originKey = columnKeyOfCard(baseCardIds, activeKey);
      const targetKey = columnKeyOfOver(baseCardIds, overKey) || originKey;
      if (!originKey || !targetKey) return null;

      const originIndex = baseCardIds[originKey].indexOf(activeKey);
      const targetIds = baseCardIds[targetKey] || [];
      const overIndex = overKey.startsWith(CARD)
        ? targetIds.indexOf(overKey)
        : -1;

      let toIndex;
      if (targetKey === originKey) {
        /* Reordering in place: the list still holds the card, so landing on a
         * neighbour means taking its index — a move within the array. */
        toIndex = overIndex === -1 ? targetIds.length - 1 : overIndex;
      } else if (overIndex === -1) {
        /* Dropped on the column's empty space: append. */
        toIndex = targetIds.length;
      } else {
        /* On a card of another column: above or below it, depending on which
         * half of that card the dragged one has reached. */
        const translated = active.rect.current?.translated;
        const isBelow =
          translated && over.rect
            ? translated.top > over.rect.top + over.rect.height / 2
            : false;
        toIndex = overIndex + (isBelow ? 1 : 0);
      }

      return { originKey, targetKey, originIndex, toIndex };
    },
    [baseCardIds],
  );

  const handleDragEnd = ({ active, over }) => {
    const activeKey = String(active.id);

    onDragEnd?.();

    if (!over) {
      resetDrag();
      return;
    }

    const overKey = String(over.id);

    if (activeKey.startsWith(COL)) {
      const overColumn = columnKeyOfOver(baseCardIds, overKey);
      const fromDisplay = baseColumnIds.indexOf(activeKey);
      const toDisplay = baseColumnIds.indexOf(`${COL}${overColumn}`);

      if (fromDisplay !== -1 && toDisplay !== -1 && fromDisplay !== toDisplay) {
        onColumnMove?.({
          columnId: columnById.get(activeKey.slice(COL.length)),
          fromIndex: toColumnIndex(fromDisplay),
          toIndex: toColumnIndex(toDisplay),
        });
      }
      resetDrag();
      return;
    }

    const plan = planDrop(active, over);
    if (!plan) {
      resetDrag();
      return;
    }
    const { originKey, targetKey, originIndex, toIndex } = plan;

    if (originKey === targetKey && originIndex === toIndex) {
      resetDrag();
      return;
    }

    onCardMove?.({
      cardId: getCardId(cardById.get(activeKey) || {}),
      fromColumnId: columnById.get(originKey),
      toColumnId: columnById.get(targetKey),
      fromIndex: originIndex,
      toIndex,
    });

    resetDrag();
  };

  const Empty = renderEmpty || ((col, meta) => <DefaultEmpty {...meta} />);
  const columnsDraggable = Boolean(onColumnMove);
  const activeCard = activeCardKey ? cardById.get(activeCardKey) : null;

  return (
    <div
      ref={boardRef}
      className={styles.board}
      style={{ "--kanban-accent": accent || DEFAULT_ACCENT }}
      data-tour={dataTour}
    >
      <div
        ref={scrollerRef}
        className={`${styles.scroller} ds-scroll-compact ${busy ? styles.scrollerBusy : ""}`}
        dir="ltr"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={MEASURING}
          autoScroll={autoScroll}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            onDragEnd?.();
            resetDrag();
          }}
        >
          <ScrollMeasureSync rootRef={boardRef} />
          <SortableContext
            items={baseColumnIds}
            strategy={horizontalListSortingStrategy}
          >
            <div className={styles.columns}>
              {}
              {trailing && (
                <div className={styles.trailing} dir="rtl">
                  {trailing}
                </div>
              )}
              {baseColumnIds.map((columnKey) => {
                const key = columnKey.slice(COL.length);
                const column = displayColumns.find(
                  (item) => String(item.id) === key,
                );
                if (!column) return null;

                const ids = baseCardIds[key] || [];
                const cards = ids
                  .map((cardKey) => cardById.get(cardKey))
                  .filter(Boolean);

                return (
                  <SortableColumn
                    key={columnKey}
                    column={column}
                    width={width}
                    draggable={columnsDraggable && column.draggable !== false}
                    renderHeaderActions={renderHeaderActions}
                    renderColumnTop={renderColumnTop}
                    renderColumnFooter={renderColumnFooter}
                  >
                    <ColumnBody
                      column={column}
                      cards={cards}
                      cardIds={ids}
                      cardsDraggable={cardsDraggable}
                      isDraggingOver={
                        activeType === "card" && overColumnKey === key
                      }
                      insertIndex={
                        activeType === "card" && overColumnKey === key
                          ? dropIndex
                          : null
                      }
                      renderCard={renderCard}
                      renderEmpty={Empty}
                      renderFooter={renderFooter}
                    />
                  </SortableColumn>
                );
              })}
            </div>
          </SortableContext>

          {}
          <DragOverlay dropAnimation={null}>
            {activeCard ? (
              <div dir="rtl" style={{ width: width - 18 }}>
                {renderCard(activeCard, { isDragging: true })}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {scrollState.earlier && (
        <button
          type="button"
          aria-label="ستون‌های قبلی"
          className={`${styles.scrollArrow} ${styles.scrollArrowRight}`}
          onClick={() => scrollBy(SCROLL_STEP)}
        >
          <RightOutlined />
        </button>
      )}
      {scrollState.later && (
        <button
          type="button"
          aria-label="ستون‌های بعدی"
          className={`${styles.scrollArrow} ${styles.scrollArrowLeft}`}
          onClick={() => scrollBy(-SCROLL_STEP)}
        >
          <LeftOutlined />
        </button>
      )}
    </div>
  );
};

export default KanbanBoard;
